export enum SourceType {
    AMAX_UP_CLIENT = 'amax_up_client',
    AMAX_UP_SERVER = 'amax_up_server',
}

export interface Message {
    type: string
    id: string | number
    source: SourceType
    payload?: any
}

export interface Actor {
    actor: string
    permission: Permission
}

export type Permission = 'active' | 'owner'

export interface Account {
    name: string
    publicKey: string
    permission: Permission
}

export interface Action {
    account: string // 合约名称
    name: string // 合约方法名
    data: any // 合约参数
    authorization?: Actor[]
}

export enum ClientEventType {
    GET_ACCOUNT = 'getAccount',
    TRANSACT = 'transact',
    SIGN_MESSAGE = 'signMessage',
    REQUEST = 'request',
}

export enum CommonEventType {
    ERROR = 'error',
}

export enum ServerEventType {
    ACCOUNTS_CHANGED = 'accountsChanged',
}

export interface Resolver {
    resolve: any
    reject: any
}

export interface AbiBinToJsonResult {
    args: 'any'
}
export interface AbiJsonToBinResult {
    binargs: 'string'
}

export interface Abi {
    version: string
    types: {
        new_type_name: string
        type: string
    }[]
    structs: {
        name: string
        base: string
        fields: {
            name: string
            type: string
        }[]
    }[]
    actions: {
        name: string
        type: string
        ricardian_contract: string
    }[]
    tables: {
        name: string
        type: string
        index_type: string
        key_names: string[]
        key_types: string[]
    }[]
    ricardian_clauses: {
        id: string
        body: string
    }[]
    error_messages: {
        error_code: number
        error_msg: string
    }[]
    abi_extensions: {
        tag: number
        value: string
    }[]
    variants?: {
        name: string
        types: string[]
    }[]
    action_results?: {
        name: string
        result_type: string
    }[]
    kv_tables?: {
        [key: string]: {
            type: string
            primary_index: {
                name: string
                type: string
            }
            secondary_indices: {
                [key: string]: {
                    type: string
                }
            }[]
        }
    }[]
}

export interface GetAbiResult {
    account_name: string
    abi?: Abi
}

export interface AccountResourceInfo {
    used: number
    available: number
    max: number
    last_usage_update_time?: string
    current_used?: number
}
export interface ResourceOverview {
    owner: string
    ram_bytes: number
    net_weight: string
    cpu_weight: string
}
export interface ResourceDelegation {
    from: string
    to: string
    net_weight: string
    cpu_weight: string
}

export interface RefundRequest {
    owner: string
    request_time: string
    net_amount: string
    cpu_amount: string
}
export interface GetAccountResult {
    account_name: string
    head_block_num: number
    head_block_time: string
    privileged: boolean
    last_code_update: string
    created: string
    core_liquid_balance?: string
    ram_quota: number
    net_weight: number
    cpu_weight: number
    net_limit: AccountResourceInfo
    cpu_limit: AccountResourceInfo
    ram_usage: number
    permissions: Permission[]
    total_resources: ResourceOverview | null
    self_delegated_bandwidth: ResourceDelegation | null
    refund_request: RefundRequest | null
    voter_info: any
    rex_info: any
}
export interface Authorization {
    actor: string
    permission: string
}

export interface AccountResult {
    account_name: string
    permission_name: string
    authorizing_account?: Authorization
    authorizing_key?: string
    weight: number
    threshold: number
}
export interface GetAccountsByAuthorizersResult {
    accounts: AccountResult[]
}

export interface GetActivatedProtocolFeaturesParams {
    limit?: number
    search_by_block_num?: boolean
    reverse?: boolean
    lower_bound?: number
    upper_bound?: number
}

export interface ActivatedProtocolFeature {
    feature_digest: string
    activation_ordinal: number
    activation_block_num: number
    description_digest: string
    dependencies: string[]
    protocol_feature_type: string
    specification: {
        name: string
        value: string
    }
}
export interface GetActivatedProtocolFeaturesResult {
    activated_protocol_features: ActivatedProtocolFeature[]
    more?: number
}
export interface SignedBlockHeader extends BlockHeader {
    producer_signature: string
}
export interface ProducerKey {
    producer_name: string
    block_signing_key: string
}
export interface ProducerScheduleType {
    version: number
    producers: ProducerKey[]
}
export interface BlockHeader {
    timestamp: string
    producer: string
    confirmed: number
    previous: string
    transaction_mroot: string
    action_mroot: string
    schedule_version: number
    new_producers?: ProducerScheduleType
    header_extensions: [number, string][]
}
export interface ScheduleInfo {
    schedule_lib_num: number
    schedule_hash: string
    schedule: ProducerScheduleType
}
export interface ProtocolFeatureActivationSet {
    protocol_features: string[]
}
export interface KeyWeight {
    key: string
    weight: number
}
export interface BlockSigningAuthority {
    threshold: number
    keys: KeyWeight[]
}
export interface ProducerAuthority {
    producer_name: string
    authority: [number | string, BlockSigningAuthority]
}
export interface ProducerAuthoritySchedule {
    version: number
    producers: ProducerAuthority[]
}
export interface IncrementalMerkle {
    _active_nodes: string[]
    _node_count: number
}
export interface SecurityGroupInfo {
    version: number
    participants: string[]
}
export interface StateExtension {
    security_group_info: SecurityGroupInfo
}
export interface GetBlockHeaderStateResult {
    id: string
    header: SignedBlockHeader
    pending_schedule: ScheduleInfo
    activated_protocol_features: ProtocolFeatureActivationSet
    additional_signatures: string[]
    block_num: number
    dpos_proposed_irreversible_blocknum: number
    dpos_irreversible_blocknum: number
    active_schedule: ProducerAuthoritySchedule
    blockroot_merkle: IncrementalMerkle
    producer_to_last_produced: Map<string, number>
    producer_to_last_implied_irb: Map<string, number>
    valid_block_signing_authority: any
    confirm_count: number[]
    state_extension: [number, StateExtension]
}
export interface GetBlockInfoResult {
    timestamp: string
    producer: string
    confirmed: number
    previous: string
    transaction_mroot: string
    action_mroot: string
    schedule_version: number
    producer_signature: string
    id: string
    block_num: number
    ref_block_num: number
    ref_block_prefix: number
}
export interface GetBlockResult {
    timestamp: string
    producer: string
    confirmed: number
    previous: string
    transaction_mroot: string
    action_mroot: string
    schedule_version: number
    new_producers: ProducerScheduleType | null
    producer_signature: string
    transactions: any
    id: string
    block_num: number
    ref_block_prefix: number
}
export interface GetCodeResult {
    account_name: string
    code_hash: string
    wast: string
    wasm: string
    abi?: Abi
}
export interface GetCodeHashResult {
    account_name: string
    code_hash: string
}
export interface GetCurrencyStatsResult {
    [key: string]: {
        supply: string
        max_supply: string
        issuer: string
    }
}
export interface GetInfoResult {
    server_version: string
    chain_id: string
    head_block_num: number
    last_irreversible_block_num: number
    last_irreversible_block_id: string
    last_irreversible_block_time?: string
    head_block_id: string
    head_block_time: string
    head_block_producer: string
    virtual_block_cpu_limit: number
    virtual_block_net_limit: number
    block_cpu_limit: number
    block_net_limit: number
    server_version_string?: string
    fork_db_head_block_num?: number
    fork_db_head_block_id?: string
    server_full_version_string?: string
    first_block_num?: number
}
export interface GetProducerScheduleResult {
    active: ProducerAuthoritySchedule | null
    pending: ProducerAuthoritySchedule | null
    proposed: ProducerAuthoritySchedule | null
}
export interface ProducerDetails {
    owner: string
    producer_authority?: any[]
    url: string
    is_active?: number
    total_votes: string
    producer_key: string
    unpaid_blocks?: number
    last_claim_time?: string
    location?: number
}
export interface GetProducersResult {
    rows: ProducerDetails[]
    total_producer_vote_weight: string
    more: string
}
export interface GetRawCodeAndAbiResult {
    account_name: string
    wasm: string
    abi: string
}
export interface BinaryAbi {
    /** account which has deployed the ABI */
    accountName: string
    /** abi in binary form */
    abi: Uint8Array
}
export interface GetRawAbiResult {
    account_name: string
    code_hash: string
    abi_hash: string
    abi: string
}
export interface ProcessedAction {
    account: string
    name: string
    authorization: Authorization[]
    data?: any
    hex_data?: string
}
export interface ProcessedTransaction {
    expiration?: string
    ref_block_num?: number
    ref_block_prefix?: number
    max_net_usage_words?: number
    max_cpu_usage_ms?: number
    delay_sec?: number
    context_free_actions?: ProcessedAction[]
    context_free_data?: Uint8Array[]
    actions: ProcessedAction[]
    transaction_extensions?: [number, string][]
}
export interface DeferredTransaction extends ProcessedTransaction {
    deferred_transaction_generation?: {
        sender_trx_id: string
        sender_id: string
        sender: string
    }
}
export interface GeneratedTransaction {
    trx_id: string
    sender: string
    sender_id: string
    payer: string
    delay_until: string
    expiration: string
    published: string
    packed_trx?: string[]
    transaction?: DeferredTransaction[]
}
export interface GetScheduledTransactionsResult {
    transactions: GeneratedTransaction[]
    more: string
}
export interface GetTableRowsResult {
    rows: any[]
    more: boolean
    next_key: string
    next_key_bytes: string
}
export interface GetTableByScopeResultRow {
    code: string
    scope: string
    table: string
    payer: string
    count: number
}
export interface GetTableByScopeResult {
    rows: GetTableByScopeResultRow[]
    more: string
}
export interface AuthorityProviderArgs {
    /** Transaction that needs to be signed */
    transaction: any
    /** Public keys associated with the private keys that the `SignatureProvider` holds */
    availableKeys: string[]
}
export interface PushTransactionArgs {
    signatures: string[]
    compression?: number
    serializedTransaction: Uint8Array
    serializedContextFreeData?: Uint8Array
}
export interface PushTransactionArgs {
    signatures: string[]
    compression?: number
    serializedTransaction: Uint8Array
    serializedContextFreeData?: Uint8Array
}
export interface TransactionReceiptHeader {
    status: string
    cpu_usage_us: number
    net_usage_words: number
}
export interface TransactionHeader {
    expiration: string
    ref_block_num: number
    ref_block_prefix: number
}
export interface AccountDelta {
    account: string
    delta: number
}
export interface AuthSequence {
    account: string
    sequence: number
}
export interface ActionReceipt {
    receiver: string
    act_digest: string
    global_sequence: number
    recv_sequence: number
    auth_sequence: [string, number][]
    code_sequence: number
    abi_sequence: number
}
export interface ActionTrace {
    action_ordinal: number
    creator_action_ordinal: number
    closest_unnotified_ancestor_action_ordinal: number
    receipt: ActionReceipt
    receiver: string
    act: ProcessedAction
    context_free: boolean
    elapsed: number
    console: string
    trx_id: string
    block_num: number
    block_time: string
    producer_block_id: string | null
    account_ram_deltas: AccountDelta[]
    account_disk_deltas: AccountDelta[]
    except: any
    error_code: number | null
    return_value?: any
    return_value_hex_data?: string
    return_value_data?: any
    inline_traces?: ActionTrace[]
}
export interface TransactionTrace {
    id: string
    block_num: number
    block_time: string
    producer_block_id: string | null
    receipt: TransactionReceiptHeader | null
    elapsed: number
    net_usage: number
    scheduled: boolean
    action_traces: ActionTrace[]
    account_ram_delta: AccountDelta | null
    except: string | null
    error_code: number | null
    bill_to_accounts: string[]
}
export interface TransactResult {
    transaction_id: string
    processed: TransactionTrace
}
export interface ReadOnlyTransactResult {
    head_block_num: number
    head_block_id: string
    last_irreversible_block_num: number
    last_irreversible_block_id: string
    code_hash: string
    pending_transactions: string[]
    result: TransactionTrace
}
export interface DBSizeIndexCount {
    index: string
    row_count: number
}
export interface DBSizeGetResult {
    free_bytes: number
    used_bytes: number
    size: number
    indices: DBSizeIndexCount[]
}
export interface TraceApiAction {
    global_sequence: number
    receiver: string
    account: string
    action: string
    authorization: Authorization[]
    data: any
    return_value: any
}
export interface TraceApiTransactionHeader {
    expiration: string
    ref_block_num: number
    ref_block_prefix: number
    max_net_usage_words: number
    max_cpu_usage_ms: number
    delay_sec: number
}
export interface TraceApiTransaction {
    id: string
    actions: TraceApiAction[]
    status?: string
    cpu_usage_us?: number
    net_usage_words?: number
    signatures?: string[]
    transaction_header?: TraceApiTransactionHeader
    bill_to_accounts: string[]
}
export interface TraceApiGetBlockResult {
    id: string
    number: number
    previous_id: string
    status: string
    timestamp: string
    producer: string
    transaction_mroot?: string
    action_mroot?: string
    schedule_version: number
    transactions: TraceApiTransaction
}
export interface JsonRpc {
    abi_bin_to_json(code: string, action: string, binargs: string): Promise<AbiBinToJsonResult>
    abi_json_to_bin(code: string, action: string, args: any[]): Promise<AbiJsonToBinResult>
    /** Raw call to `/v1/chain/get_abi` */
    get_abi(accountName: string): Promise<GetAbiResult>
    /** Raw call to `/v1/chain/get_account` */
    get_account(accountName: string): Promise<GetAccountResult>
    /** Raw call to `/v1/chain/get_accounts_by_authorizers` */
    get_accounts_by_authorizers(
        accounts: Authorization[],
        keys: string[]
    ): Promise<GetAccountsByAuthorizersResult>
    /** Raw call to `get_activated_protocol_features` */
    get_activated_protocol_features({
        limit,
        search_by_block_num,
        reverse,
        lower_bound,
        upper_bound,
    }: GetActivatedProtocolFeaturesParams): Promise<GetActivatedProtocolFeaturesResult>
    /** Raw call to `/v1/chain/get_block_header_state` */
    get_block_header_state(blockNumOrId: number | string): Promise<GetBlockHeaderStateResult>
    /** Raw call to `/v1/chain/get_block_info` */
    get_block_info(blockNum: number): Promise<GetBlockInfoResult>
    /** Raw call to `/v1/chain/get_block` */
    get_block(blockNumOrId: number | string): Promise<GetBlockResult>
    /** Raw call to `/v1/chain/get_code` */
    get_code(accountName: string): Promise<GetCodeResult>
    /** Raw call to `/v1/chain/get_code_hash` */
    get_code_hash(accountName: string): Promise<GetCodeHashResult>
    /** Raw call to `/v1/chain/get_currency_balance` */
    get_currency_balance(code: string, account: string, symbol?: string): Promise<string[]>
    /** Raw call to `/v1/chain/get_currency_stats` */
    get_currency_stats(code: string, symbol: string): Promise<GetCurrencyStatsResult>
    /** Raw call to `/v1/chain/get_info` */
    get_info(): Promise<GetInfoResult>
    /** Raw call to `/v1/chain/get_producer_schedule` */
    get_producer_schedule(): Promise<GetProducerScheduleResult>
    /** Raw call to `/v1/chain/get_producers` */
    get_producers(json?: boolean, lowerBound?: string, limit?: number): Promise<GetProducersResult>
    /** Raw call to `/v1/chain/get_raw_code_and_abi` */
    get_raw_code_and_abi(accountName: string): Promise<GetRawCodeAndAbiResult>
    /** calls `/v1/chain/get_raw_code_and_abi` and pulls out unneeded raw wasm code */
    getRawAbi(accountName: string): Promise<BinaryAbi>
    /** Raw call to `/v1/chain/get_raw_abi` */
    get_raw_abi(accountName: string): Promise<GetRawAbiResult>
    /** Raw call to `/v1/chain/get_scheduled_transactions` */
    get_scheduled_transactions(
        json?: boolean,
        lowerBound?: string,
        limit?: number
    ): Promise<GetScheduledTransactionsResult>
    /** Raw call to `/v1/chain/get_table_rows` */
    get_table_rows({
        json,
        code,
        scope,
        table,
        lower_bound,
        upper_bound,
        index_position,
        key_type,
        limit,
        reverse,
        show_payer,
    }: any): Promise<GetTableRowsResult>
    /** Raw call to `/v1/chain/get_kv_table_rows` */
    get_kv_table_rows({
        json,
        code,
        table,
        index_name,
        encode_type,
        index_value,
        lower_bound,
        upper_bound,
        limit,
        reverse,
        show_payer,
    }: any): Promise<GetTableRowsResult>
    /** Raw call to `/v1/chain/get_table_by_scope` */
    get_table_by_scope({
        code,
        table,
        lower_bound,
        upper_bound,
        limit,
    }: any): Promise<GetTableByScopeResult>
    /** Get subset of `availableKeys` needed to meet authorities in `transaction`. Implements `AuthorityProvider` */
    getRequiredKeys(args: AuthorityProviderArgs): Promise<string[]>
    /** Push a serialized transaction (replaced by send_transaction, but returned format has changed) */
    push_transaction({
        signatures,
        compression,
        serializedTransaction,
        serializedContextFreeData,
    }: PushTransactionArgs): Promise<TransactResult>
    /** Raw call to `/v1/chain/push_ro_transaction */
    push_ro_transaction(
        {signatures, compression, serializedTransaction}: PushTransactionArgs,
        returnFailureTraces?: boolean
    ): Promise<ReadOnlyTransactResult>
    push_transactions(transactions: PushTransactionArgs[]): Promise<TransactResult[]>
    /** Send a serialized transaction */
    send_transaction({
        signatures,
        compression,
        serializedTransaction,
        serializedContextFreeData,
    }: PushTransactionArgs): Promise<TransactResult>
    /** Raw call to `/v1/db_size/get` */
    db_size_get(): Promise<DBSizeGetResult>
    /** Raw call to `/v1/trace_api/get_block` */
    trace_get_block(block_num: number): Promise<TraceApiGetBlockResult>
    /** Raw call to `/v1/history/get_actions` */
    history_get_actions(
        accountName: string,
        pos?: number,
        offset?: number
    ): Promise<GetActionsResult>
    /** Raw call to `/v1/history/get_transaction` */
    history_get_transaction(id: string, blockNumHint?: number): Promise<GetTransactionResult>
    /** Raw call to `/v1/history/get_key_accounts` */
    history_get_key_accounts(publicKey: string): Promise<GetKeyAccountsResult>
    /** Raw call to `/v1/history/get_controlled_accounts` */
    history_get_controlled_accounts(
        controllingAccount: string
    ): Promise<GetControlledAccountsResult>
}
export interface GetControlledAccountsResult {
    controlled_accounts: string[]
}

export interface GetKeyAccountsResult {
    account_names: string[]
}
export interface GetTransactionResult {
    id: string
    trx: any
    block_time: string
    block_num: number
    last_irreversible_block: number
    traces: any[]
}

export interface OrderedActionResult {
    global_action_seq: number
    account_action_seq: number
    block_num: number
    block_time: string
    action_trace: any
}
export interface GetActionsResult {
    actions: OrderedActionResult[]
    last_irreversible_block: number
    time_limit_exceeded_error?: boolean
}
